import { ThumbnailLogicService } from '@src/slide/content-viewer/service/canvas-renderer/entry/thumbnail-mobile/logic/thumbnail-logic-service';
import { SlideComponent } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/slide';
import { Application, Container } from '@tencent/alloy-renderer';
import { ISlideModel } from '@src/slide/domain/ui-model/element-modeles/Interface/slide-model.interface';
import { reaction } from 'mobx';
import TaskManager, { TaskPriority } from '@src/slide/infrastructure/module/dependent/taskManager';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import { IEditorAppServices } from '@src/slide/application/entry/toc/editor/base/base-editor-app-service.interface';
import { IMobileEditorAppServices } from '@src/slide/application/entry/toc/editor/mobile/mobile-editor-app-service.interface';
import { ISlideComponent } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/slide/types';

// 前多少页canvas转图片以同步api方式
const SYNC_CANVAS_TO_IMG_NUM = 10;

export class RenderArea extends Container {
    public slideComponent: SlideComponent;
    public application: Application<any> | null;
    public renderVersion: number;
    public callbacks: ((url?: string, slideComponent?: SlideComponent) => void)[];    // slide 渲染完生成图回调
    public slideImageSrc: string;
    public canvasWrapper: HTMLElement;
    private slideModel: ISlideModel;
    private getPageScale: () => number;
    private region: string;
    private mobileEditMode: () => any;
    private syncBuild: boolean;
    private handleSlideChange: (slide: ISlideModel) => void;
    private onDidRenderCallback: (slideComponent: ISlideComponent) => void;
    private renderRequested: boolean;
    private lastScale?: number;

    public constructor(
        slideModel: ISlideModel,
        getPageScale: () => number,
        region: string,
        enableEvent: boolean,
        mobileEditMode: () => any,
        handleSlideChange: (slide: ISlideModel) => void,
        onDidRenderCallback: (slideComponent: ISlideComponent) => void,
    ) {
        super();
        this.slideModel = slideModel;
        this.getPageScale = getPageScale;
        this.region = region;
        this.mobileEditMode = mobileEditMode;
        this.handleSlideChange = handleSlideChange;
        this.onDidRenderCallback = onDidRenderCallback;
        this.renderVersion = 0;
        // eslint-disable-next-line no-underscore-dangle
        this._addUpdateListener(this.onUpdate);
        this.callbacks = [];
        this.init();
    }

    public mount = (): void => {
        // 如果还没有创建树，同步创建
        if (!this.slideComponent) {
            this.syncBuild = true;
            this.doAfterConverted();
        }
    };

    public unmount = (): void => {
        if (this.canvasWrapper) {
            const canvasDom = this.application?.view?.native;
            if (canvasDom?.style.display === 'block') {
                canvasDom.style.display = 'none';
            }
            if (this.canvasWrapper.hasChildNodes()) {
                this.canvasWrapper.removeChild(this.canvasWrapper.childNodes[0]);
            }
        }
        this.lastScale = this.getPageScale();
    };

    public dispose = (): void => {
        this.slideComponent?.dispose();
        this._removeUpdateListener(this.onUpdate);
    };

    /**
     * 触发渲染，下一帧生成图片
     */
    public render(): void {
        if (!this.application) return;
        super.render(this.application.view);

        if (this.slideModel.childrenConverted && this.slideComponent.hasBuild) {
            if (this.renderRequested) return;
            this.renderRequested = true;

            requestAnimationFrame(() => {
                this.renderRequested = false;
                this.renderVersion += 1;
                if (this.application) {
                    try {
                        this.releaseImage();
                        this.generateImage(this.application);
                    } catch (e) {
                        window.log.error(`[generateImage-error]${e}`);
                    }
                }
            });
        }
    }

    public generateImage = (application: Application): void => {
        const version = this.renderVersion;
        const prevTaskKey = `generate-image-${this.slideModel.key}-${this.renderVersion - 1}`;
        const task = TaskManager.getTaskWithKey(prevTaskKey);
        if (task) {
            TaskManager.cancelTaskByKey(prevTaskKey);
        }
        const currentTaskKey = `generate-image-${this.slideModel.key}-${this.renderVersion}`;
        TaskManager.addTaskWithBlock(currentTaskKey, TaskPriority.HIGH, async () => {
            if (version !== this.renderVersion) return;
            if (this.app !== application) return;
            try {
                let imgSrc: string | null = null;
                if (this.needSyncToImg) {
                    imgSrc = this.syncCanvasToImg(application.view.native as HTMLCanvasElement, version);
                } else {
                    imgSrc = await this.asyncCanvasToImg(application.view.native as HTMLCanvasElement, version);
                }
                if (imgSrc) {
                    this.slideImageSrc = imgSrc;
                    this.handleSlideChange(this.slideModel);
                }
            } catch (e) {
                window.log.error(`[generateImage-toBlob-error]${e}`);
            }
        });
    };

    /**
     * 没有缓存图片或者 scale 变化时，需要重新渲染
     */
    public needRender = (): boolean => !this.slideImageSrc || this.lastScale !== this.getPageScale();

    /**
     * 当前页的canvas转图片是否以同步的形式进行
     * 前10页同步后面的异步，因为同步转图会以字符串的形式保存图片，会占用更多的内存
     * 而同步转图的好处是避免异步出图的过程中插入其他任务导致出图时间变长
     * @private
     */
    private get needSyncToImg(): boolean {
        const index = this.slideModel.parent?.children.findIndex(e => e === this.slideModel) || 0;
        return index < SYNC_CANVAS_TO_IMG_NUM;
    }

    /**
     * 同步转图
     * @param canvas
     * @param version
     */
    private syncCanvasToImg = (
        canvas: HTMLCanvasElement,
        version: number,
    ): null | string => {
        // this.slideImageSrc不存在的时候即使不是最新的版本，也要渲染一次
        // 避免部分页一直被delay，从而造成后页先出的现象
        if (version !== this.renderVersion && this.slideImageSrc) {
            return null;
        }
        return canvas.toDataURL('image/jpeg');
    };

    /**
     * 异步转图
     * @param canvas
     * @param version
     */
    private asyncCanvasToImg = async (
        canvas: HTMLCanvasElement,
        version: number,
    ): Promise<null | string> => new Promise((resolve) => {
        canvas.toBlob((blob) => {
            if (blob) {
                // this.slideImageSrc不存在的时候即使不是最新的版本，也要渲染一次
                // 避免部分页一直被delay，从而造成后页先出的现象
                if (version !== this.renderVersion && this.slideImageSrc) {
                    resolve(null);
                    return;
                }
                resolve(URL.createObjectURL(blob));
            }
        });
    });

    /**
     * 子树发生了变更，清除图片缓存
     */
    private onUpdate = () => {
        this.releaseImage();
    };

    /**
     * 释放图片资源
     * @private
     */
    private releaseImage = () => {
        if (this.slideImageSrc) {
            if (/^blob:/.test(this.slideImageSrc)) {
                URL.revokeObjectURL(this.slideImageSrc);
            }
            this.slideImageSrc = '';
        }
    };

    private init = () => {
        if (this.slideModel.childrenConverted) {
            this.doAfterConverted();
        } else {
            // 子元素没有转换完成，先不创建
            this.addConvertObserver();
        }
    };

    /**
     * 监听当前页面的子元素转换完成
     */
    private addConvertObserver = (): void => {
        const convertedReaction = reaction(
            () => this.slideModel.childrenConverted,
            (converted) => {
                if (converted) {
                    this.doAfterConverted();
                    // 释放reaction监听
                    convertedReaction?.();
                }
            },
        );
    };

    private doAfterConverted = (): void => {
        this.build();
    };

    private getApplication = () => this.application as Application;

    /**
     * 根据数据初始化渲染层
     * @private
     */
    private build = () => {
        if (this.slideComponent) {
            return;
        }
        const logicService = new ThumbnailLogicService();
        const index = this.getAppServices().getUiDataService()
            .presentation.children.findIndex(item => item === this.slideModel);
        this.slideComponent = new SlideComponent({
            slideModel: this.slideModel,
            getPageScale: this.getPageScale,
            getApplication: this.getApplication,
            region: this.region,
            fromScale: 1,
            logicService,
            asyncBuild: index !== 0 && !this.syncBuild,
            mobileEditMode: this.mobileEditMode,
            onWillRenderCallback: this.onWillRenderCallback,
            onDidRenderCallback: this.canvasRenderCallback,
            infrastructureService: this.getAppServices().getInfrastructureService(),
            uiDataService: this.getAppServices()?.getUiDataService(),
            selectionService: this.getAppServices()?.viewableServiceLayer?.selectionService,
            mediaManager: this.getAppServices().viewableServiceLayer?.mediaManager,
            attachmentManager: (this.getAppServices() as IMobileEditorAppServices)
                .viewableServiceLayer?.attachmentManager,
            editingStateManager: this.getAppServices().viewableServiceLayer.editingStateManager,
            addonManager: this.getAppServices().viewableServiceLayer.addonManager,
            fontManager: this.getAppServices().viewableServiceLayer.fontManager,
            linkManager: this.getAppServices().viewableServiceLayer.linkManager,
        });
        this.addChild(this.slideComponent);
    };

    private onWillRenderCallback = (slideComponent: ISlideComponent) => {
        slideComponent.options.slideModel.pptEditorState = this.getAppServices()
            .viewableServiceLayer.userPriviledgeService.pptEditorState;
    };

    private canvasRenderCallback = () => {
        this.onDidRenderCallback(this.slideComponent);
    };


    private getAppServices(): IEditorAppServices {
        return AppServiceProvider.getInstance().getAppServices() as
            IEditorAppServices;
    }
}
